import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HackneyCouncilPage() {
  const faqItems = [
    {
      question: 'Hackney spends more than twelve times its DfT capital allocation — can I still claim?',
      answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 of the Highways Act 1980 — not on aggregate spend. Hackney is AMBER overall because road condition remains a concern: 19% of U-roads were in RED condition at the last survey, and A-road RED share was still 18.59% in 2023.',
    },
    {
      question: 'What if my pothole was on a residential street?',
      answer: 'U-roads make up 208.7km — 77% of Hackney\'s 271.1km carriageway network. At the last survey in 2023, 19% were in RED condition — roughly 40km of residential streets. Surveys are carried out bi-annually, so there is no condition data at all for U-roads in alternate years. If your incident fell in a blind year, the council cannot point to a contemporaneous network survey.',
    },
    {
      question: 'Does the biennial survey gap weaken Hackney\'s Section 58 defence?',
      answer: 'It can. Hackney\'s own report states surveys "are generally carried out bi-annually" — meaning A-roads, B/C roads and U-roads were not condition-surveyed in 2020, 2022 or 2024. A council relying on Section 58 must show a reasonable system for knowing road condition. Measuring 77% of the network only every other year creates structural gaps, especially when 19% of U-roads were already RED at the last reading.',
    },
    {
      question: 'Potholes rose 46% from 2022/23 to 2024/25 despite 90% preventative spend — does that help my claim?',
      answer: 'It strengthens the argument that reactive failure persists. Hackney reports 88–90% preventative maintenance in recent years, yet pothole fills climbed from 1,619 in 2022/23 to 2,357 in 2024/25 — and reactive pothole spending consumed 41.5% of the responsive budget in 2023/24. Defects are still forming faster than the system eliminates them, which is exactly where prior reports and photographic evidence decide claims.',
    },
    {
      question: 'Does Hackney\'s Section 58 streetworks protection affect my pothole claim?',
      answer: 'No — it is a different Section 58. Hackney applies Section 58 of the New Roads and Street Works Act to protect newly resurfaced roads from utility excavations for five years. That shows the council understands how repeated digging accelerates deterioration. Your claim relies on Section 58 of the Highways Act 1980 — whether the council took reasonable care to maintain the specific road. The two provisions share a number, not a defence.',
    },
    {
      question: 'A-road RED condition improved from 25.83% to 18.59% — does that weaken claims on main roads?',
      answer: 'It shows progress on TfL-surveyed A-roads, and you should expect a stronger Section 58 defence there. But 18.59% RED still means nearly one in five A-road kilometres was in poor condition at the last survey — and there was no A-road survey at all in 2020, 2022 or 2024. Claims on A-roads still turn on whether the specific defect was identified and repaired within reasonable timeframes, not on network-wide improvement trends.',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Hackney Pothole Claims | 19% of Residential Roads RED | Fixtyer"
        description="Hackney Council: AMBER DfT rating despite GREEN spend. 19% of U-roads in RED at last survey, 10,219 potholes filled in five years, and 77% of the network surveyed only every other year. See what this means for your claim."
        canonical="/councils/hackney"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Hackney', url: '/councils/hackney' },
        ]}
      />
      <FAQSchema faqs={faqItems} />
      <ServiceSchema
        councilName="Hackney Council"
        councilSlug="hackney"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/hackney"
        headline="Hackney Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Hackney</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Hackney: One in Five Residential Streets Still in RED Condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Hackney earns a GREEN spend scorecard — projecting £5.25m capital spend against just £408,000 
            from the DfT — and reports up to 90% preventative maintenance. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> because at the last biennial survey in 2023,{' '}
            <strong className="text-rose-400">19% of U-roads were in RED condition</strong> across 208.7km 
            of residential streets, pothole fills climbed back to 2,357 in 2024/25, and{' '}
            <strong className="text-amber-400">77% of the network goes unsurveyed in alternate years</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">19%</div>
            <div className="text-white font-medium mt-2">U-roads in RED at last survey (2023)</div>
            <div className="text-brand-300 text-sm mt-1">
              Roughly 40km of Hackney's residential streets rated poor — on a network where condition 
              surveys happen only every other year and 10,219 potholes were filled across five years.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A Dense Urban Network
          </h2>
          <p className="text-surface-600 mb-8">
            Hackney's own transparency report — 271km of carriageways in one of London's most densely populated boroughs
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">271.1km</div>
              <div className="text-sm text-surface-600 mt-1">Total carriageways</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">208.7km</div>
              <div className="text-sm text-surface-600 mt-1">U-roads (77% of network)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">542.2km</div>
              <div className="text-sm text-surface-600 mt-1">Footways maintained</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">35.9km</div>
              <div className="text-sm text-surface-600 mt-1">A-roads (TfL-surveyed)</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Shared Responsibility With TfL</h3>
              <p className="text-surface-600 text-sm mb-3">
                Hackney maintains the borough's Principal Road Network, but TfL manages the TfL Road Network 
                (TLRN) — the A10, A12, A503, A107 and A1202 — and conducts SCANNER condition surveys on 
                A-roads, sharing results with the borough when published.
              </p>
              <p className="text-surface-600 text-sm">
                B and C roads (26.5km) and U-roads (208.7km) are Hackney's alone — surveyed bi-annually 
                by an independent UKPMS-accredited consultant.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Beyond Carriageways</h3>
              <p className="text-amber-800 text-sm">
                Hackney also maintains approximately 11,300 streetlights, 10,500 gullies, 15,500 highway 
                trees, 33 bridges and structures, and 3,000 illuminated street furniture items — all subject 
                to scheduled inspection regimes set out in the council's own report.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Biennial survey data from Hackney's transparency report — alternate years with no readings at all
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (35.9km — 13.2% of network): improving but still high RED</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>Survey not carried out</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">25.83%</td>
                    <td className="px-4 py-2 text-center">11.06%</td>
                    <td className="px-4 py-2 text-center">63.05%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>Survey not carried out</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono font-bold">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">18.59%</td>
                    <td className="px-4 py-2 text-center font-bold">16.67%</td>
                    <td className="px-4 py-2 text-center font-bold">64.72%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>Survey not carried out</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED share fell from 25.83% to 18.59% — genuine improvement. But at the last reading, 
              nearly one in five A-road kilometres was still in poor condition, and there has been no 
              survey since 2023.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (26.5km — 9.8% of network): flat RED, slipping green</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">14.80%</td>
                    <td className="px-4 py-2 text-center">9.83%</td>
                    <td className="px-4 py-2 text-center">75.21%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15.53%</td>
                    <td className="px-4 py-2 text-center font-bold">9.94%</td>
                    <td className="px-4 py-2 text-center font-bold">74.47%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C roads show a small drift: RED up from 14.80% to 15.53%, green down from 75.21% to 74.47%. 
              Not dramatic — but not improving either.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">U-roads (208.7km — 77% of network): RED rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">Survey not carried out</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">17%</td>
                  </tr>
                  <tr className="border-b border-rose-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">Survey not carried out</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono font-bold">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">19%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">Survey not carried out</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              U-road RED condition rose from 17% to 19% — approximately 39.7km of residential streets in 
              poor condition at the last survey. The council publishes RED percentages only for U-roads, 
              not the full amber/green breakdown.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£408k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.25m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">90%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Hackney projects spending more than <span className="text-emerald-400">twelve times</span> its 
              DfT capital allocation — with revenue spend of £635,000 on top. The problem is not the 
              chequebook. U-roads are still getting worse, and potholes are climbing again.
            </p>
          </div>
        </div>
      </section>

      {/* The Biennial Blind Spot */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 208.7km Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            77% of Hackney's carriageway network — surveyed only every other year
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Surveys are generally carried out bi-annually and are detailed visual inspections carried out for Hackney Council by an independent UKPMS accredited consultant."
              source="Hackney Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Alternate-Year Gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                For A-roads, B/C roads and U-roads alike, Hackney's published data shows "Survey not carried 
                out" in 2020, 2022 and 2024. That means for incidents in those years, there is{' '}
                <strong>no network-level condition data at all</strong> — including for the 208.7km of 
                residential streets that make up most of the borough.
              </p>
              <p className="text-surface-600 text-sm">
                At the last two comparable U-road surveys, 17% and 19% were in RED condition — roughly 
                35–40km of estate roads, residential streets and minor routes.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The "Annual" Contradiction</h3>
              <p className="text-amber-800 text-sm mb-3 italic">
                "Prioritising works based on a rigorous scoring system derived from annual condition surveys 
                conducted by an independent company" — yet the condition tables show biennial surveys with 
                blank alternate years.
              </p>
              <p className="text-amber-800 text-sm">
                For a Section 58 defence, the council must demonstrate a reasonable inspection system. 
                Contradictory descriptions of survey frequency in its own published report is a question 
                worth asking.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence under the Highways Act 1980, a council must show it had a 
              reasonable system for knowing the condition of its roads. For Hackney's U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it one of the blind years?</li>
              <li>• If 19% of U-roads were RED at the last reading, what was done about yours?</li>
              <li>• How does the council track deterioration on roads it measures every other year?</li>
              <li>• Does the council's own report describe surveys as biennial or annual?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of a network it only measures in alternate years — 
              especially when RED share is rising.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole Data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            10,219 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from Hackney's own report — falling, then climbing back
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Cost</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">% of responsive budget</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold">2,427</td>
                  <td className="px-6 py-4 text-center">£283k</td>
                  <td className="px-6 py-4 text-center">24.60%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,798</td>
                  <td className="px-6 py-4 text-center">£284k</td>
                  <td className="px-6 py-4 text-center">24.70%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,619</td>
                  <td className="px-6 py-4 text-center">£380k</td>
                  <td className="px-6 py-4 text-center">33.00%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,018</td>
                  <td className="px-6 py-4 text-center">£478k</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">41.50%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,357</td>
                  <td className="px-6 py-4 text-center">£379k</td>
                  <td className="px-6 py-4 text-center">33.00%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10,219</td>
                  <td className="px-6 py-4 text-center font-bold">£1.80m</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Rebound Pattern</h3>
              <p className="text-surface-600 text-sm">
                Pothole fills fell from 2,427 to 1,619 between 2020/21 and 2022/23 — then climbed 46% to 
                2,357 by 2024/25. Reactive pothole spending hit 41.5% of the responsive budget in 2023/24, 
                its highest point in five years. A network where defects rebound after a lull is one where 
                prior reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Preventative Paradox</h3>
              <p className="text-amber-800 text-sm">
                Hackney reports 86–90% preventative maintenance across the five-year spending table — yet 
                pothole volumes and reactive budget share both rose again. The council's own stated aim is 
                to move "away from a primary reliance on reactive repairs." The pothole numbers suggest 
                that shift is incomplete.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Streetworks & Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Two Different Section 58s
          </h2>
          <p className="text-surface-600 mb-8">
            Hackney protects its resurfaced roads from utilities — while defending pothole claims under a different statute
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The council applies Section 58 restrictions on newly resurfaced or upgraded roads, offering five years' protection against non-emergency utility works, helping preserve asset condition and minimise disruption."
              source="Hackney Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Hackney Council is committed to a strategic, risk-based approach to highway maintenance, aligning with industry best practice in asset management. Our aim is to increase investment in preventative maintenance, moving away from a primary reliance on reactive repairs. This shift is designed to prolong the life of our highway assets, ultimately delivering better long-term value for money by reducing the need for expensive localised interim repairs or extensive structural reconstruction in the future."
              source="Hackney Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What Streetworks Section 58 Shows</h3>
              <p className="text-surface-600 text-sm mb-3">
                Hackney applies Section 58 of the New Roads and Street Works Act to protect freshly 
                resurfaced roads from utility excavations for five years. That is an admission that 
                repeated digging accelerates deterioration — and that the council actively manages that risk 
                on roads it has just invested in.
              </p>
              <p className="text-surface-600 text-sm">
                The council coordinates streetworks through LondonWorks, holds regular meetings with 
                statutory undertakers, and reviews all permit applications before approval.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What Highways Act Section 58 Means For You</h3>
              <p className="text-amber-800 text-sm mb-3">
                Your pothole claim turns on Section 58 of the Highways Act 1980 — whether Hackney took 
                reasonable care to maintain the specific road. The council's streetworks protections show 
                it understands how excavations damage roads. The question is whether it applied the same 
                rigour to inspecting and repairing the defect that damaged your vehicle.
              </p>
              <p className="text-amber-800 text-sm">
                If your road was recently resurfaced and then dug up by a utility company, both the 
                council's streetworks regime and the reinstatement quality become relevant to your claim.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">2025/26 Planned Programme</h3>
            <p className="text-brand-200 mb-4">
              Hackney's forward plan for 2025/26, in the council's own words:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Carriageway — 18 roads scheduled for resurfacing</li>
              <li>• Footway — 10 footways planned for renewal</li>
            </ul>
            <p className="text-brand-200 text-sm">
              The scope of the planned maintenance programme "is determined by the available budget and 
              prioritised based on these condition scores" — on a 271km network where 19% of U-roads were 
              already RED. Eighteen carriageway resurfacing schemes across a borough of this density is 
              selective, not comprehensive.
            </p>
          </div>
        </div>
      </section>

      {/* Spending table */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital and revenue figures from Hackney's transparency report — DfT allocation vs actual spend
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£4,000</td>
                  <td className="px-4 py-3 text-center">£635</td>
                  <td className="px-4 py-3 text-center">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£4,000</td>
                  <td className="px-4 py-3 text-center">£635</td>
                  <td className="px-4 py-3 text-center">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£128</td>
                  <td className="px-4 py-3 text-center">£4,500</td>
                  <td className="px-4 py-3 text-center">£635</td>
                  <td className="px-4 py-3 text-center">88%</td>
                  <td className="px-4 py-3 text-center">12%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£128</td>
                  <td className="px-4 py-3 text-center">£4,500</td>
                  <td className="px-4 py-3 text-center">£635</td>
                  <td className="px-4 py-3 text-center">88%</td>
                  <td className="px-4 py-3 text-center">12%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26</td>
                  <td className="px-4 py-3 text-center font-bold">£408</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£5,250</td>
                  <td className="px-4 py-3 text-center">£635</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">90%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Our strategic investment in preventative maintenance, guided by the Capital Footway and Carriageway programme, has focused on improving long-term asset performance and reducing reactive maintenance spending in highways maintenance."
            source="Hackney Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Hackney invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend far exceeds DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 86–90% of spend classed as preventative across five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road RED share improved from 25.83% to 18.59%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management approach and streetworks coordination
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Independent UKPMS-accredited condition surveys
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on A-roads and recently resurfaced routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-road RED share rose from 17% to 19%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 77% of network surveyed only every other year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 10,219 potholes filled in five years — defects rebounding since 2022/23
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive pothole budget hit 41.5% in 2023/24 despite preventative spend claims
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Biennial vs "annual" survey frequency contradiction in the council's own report
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only 18 carriageway resurfacing schemes planned for 2025/26 on a 271km network
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a documented asset management strategy, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, emails to Highway.Inspectors@hackney.gov.uk) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the biennial survey gap is your strongest structural argument</li>
              <li>• Whether utility excavations preceded the defect — Hackney's own streetworks data may help</li>
              <li>• Whether your incident fell in a survey blind year (2020, 2022 or 2024)</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Hackney's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Hackney?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road biennial survey argument</li>
                <li>• No prior-report search</li>
                <li>• No streetworks excavation analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road 19% RED condition documented</li>
                <li>✅ Biennial survey gap argued</li>
                <li>✅ 10,219 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Hackney</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=hackney"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Hackney Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqItems.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://docs.google.com/document/d/1_1NLfFmzTqPU0B7NPKtBeFMxcjAaDRzUIlgf5cl733I/edit"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Hackney Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
